import type { PageNameType } from './clientConfiguration/clientConfigurationSlice';
import type { RootState } from './store';

// Sub-resources normally share their parent's slice (split on '/'), but
// cardtimes has a different DTO than cards, so it gets its own isolated slice
// to avoid cross-contaminating structure/preset state when switching tabs.
const DOMAIN_OVERRIDES: Partial<Record<PageNameType, keyof RootState>> = {
	'cards/cardtimes': 'cardtimes',
	'contracts/details': 'contractsDetails',
	'destPoint/details': 'destPointDetails',
	'products/groups': 'productsGroups',
};

export const getBaseDomain = (pageName: PageNameType): keyof RootState =>
	DOMAIN_OVERRIDES[pageName] ?? (pageName.split('/')[0] as keyof RootState);

// Sub-resource pages live on their parent's backend controller, where the
// action name carries the sub-resource suffix (e.g. Cards.GetCardTimesAll,
// Products.CreateGroups). A generic `{pageName}.{action}` check like
// 'cards/cardtimes.GetAll' can never match a seeded role, so each sub-page
// maps its generic actions to the real backend action names here. Actions
// absent from a map have no backend endpoint (e.g. cardtimes is read-only),
// so they must resolve to a role nobody can hold.
const SUB_RESOURCE_ROLE_ACTIONS: Record<string, Record<string, string>> = {
	'cards/cardtimes': {
		Get: 'GetCardTimes',
		GetAll: 'GetCardTimesAll',
		Excel: 'ExcelCardTimes',
		GetStructure: 'GetCardtimesStructure',
	},
	'products/groups': {
		Get: 'GetGroup',
		GetAll: 'GetAllGroups',
		Create: 'CreateGroups',
		Update: 'UpdateGroups',
		Patch: 'PatchGroups',
		Delete: 'DeleteGroups',
		GetStructure: 'GetStructureGroups',
	},
	'contracts/details': {
		Get: 'GetDetails',
		GetAll: 'GetDetailsAll',
		Create: 'CreateDetails',
		Update: 'UpdateDetails',
		GetStructure: 'GetStructureDetails',
	},
	'destPoint/details': {
		Get: 'GetDetails',
		GetAll: 'GetAllDetails',
		Create: 'CreateDetail',
		Update: 'UpdateDetail',
		Delete: 'DeleteDetail',
		GetStructure: 'GetStructureDetail',
	},
};

// Attachment features reuse the generic document role names, but some
// backends expose file endpoints under different action names. Only the
// listed actions are remapped — everything else keeps the generic name.
const DOCUMENT_ROLE_ACTIONS: Record<string, Record<string, string>> = {
	requestedMsg: {
		GetDocuments: 'GetFile',
		UploadDocuments: 'CreateFile',
		DeleteDocuments: 'DeleteFile',
	},
};

export type PageRole = { resource: string; action: string };

// Backend roles for runtime tables are keyed `DynamicTables.{Action}` for
// every table — a per-table check like `dynamictables/inspections.delete`
// would never match.
export const getPageRole = (pageName: string, action: string): PageRole => {
	if (pageName.startsWith('dynamicTables')) {
		return { resource: 'DynamicTables', action };
	}

	const documentAction = DOCUMENT_ROLE_ACTIONS[pageName]?.[action];
	if (documentAction) {
		return { resource: pageName, action: documentAction };
	}

	const subResourceActions = SUB_RESOURCE_ROLE_ACTIONS[pageName];
	if (subResourceActions) {
		return {
			resource: pageName.split('/')[0],
			// Empty action never passes hasRole — the backend has no such
			// endpoint for this sub-resource, so no role can grant it.
			action: subResourceActions[action] ?? '',
		};
	}

	return { resource: pageName, action };
};

// True for runtime-table rows pages ('dynamicTables/{TableName}'). Used to
// hide features the DynamicTables backend does not implement (excel export,
// reports, documents) and to adapt payload shapes (scalar FK columns).
export const isDynamicTablesPage = (pageName: string): boolean =>
	pageName.startsWith('dynamicTables/');
